import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PartDetailModal = ({ part, onClose, onConsult }) => {
  if (!part) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-honda-glow">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
            Detail Produk
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="space-y-4">
              <div className="relative h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden bg-muted">
                <Image
                  src={part?.image}
                  alt={part?.imageAlt}
                  className="w-full h-full object-cover"
                />
                {part?.isGenuine && (
                  <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-full flex items-center space-x-2 shadow-honda-subtle">
                    <Icon name="Shield" size={18} />
                    <span className="text-sm font-heading font-bold">Genuine Honda</span>
                  </div>
                )}
              </div>

              {part?.additionalImages && part?.additionalImages?.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {part?.additionalImages?.map((img, index) => (
                    <div key={index} className="h-20 rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={img?.url}
                        alt={img?.alt}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <span className="text-sm text-muted-foreground font-medium">{part?.category}</span>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mt-2">
                  {part?.name}
                </h3>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Harga</p>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary">
                    {formatPrice(part?.price)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1">Ketersediaan</p>
                  <p className={`text-lg font-heading font-bold ${part?.stock > 10 ? 'text-success' : part?.stock > 0 ? 'text-warning' : 'text-error'}`}>
                    {part?.stock > 0 ? `${part?.stock} unit tersedia` : 'Stok habis'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-heading font-bold text-foreground mb-2">Deskripsi</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {part?.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Package" size={18} color="var(--color-primary)" />
                      <span className="text-xs text-muted-foreground">Kode Part</span>
                    </div>
                    <p className="text-sm font-heading font-bold text-foreground">{part?.partCode}</p>
                  </div>

                  {part?.warranty && (
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Award" size={18} color="var(--color-primary)" />
                        <span className="text-xs text-muted-foreground">Garansi</span>
                      </div>
                      <p className="text-sm font-heading font-bold text-foreground">{part?.warranty}</p>
                    </div>
                  )}
                </div>

                {part?.specifications && (
                  <div>
                    <h4 className="text-lg font-heading font-bold text-foreground mb-3">Spesifikasi</h4>
                    <div className="space-y-2">
                      {part?.specifications?.map((spec, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <span className="text-sm text-muted-foreground">{spec?.label}</span>
                          <span className="text-sm font-heading font-semibold text-foreground">{spec?.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {part?.compatibleModels && part?.compatibleModels?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-heading font-bold text-foreground mb-3">Model Kompatibel</h4>
                    <div className="flex flex-wrap gap-2">
                      {part?.compatibleModels?.map((model, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-heading font-semibold"
                        >
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => onConsult(part)}
                  disabled={part?.stock === 0}
                >
                  Konsultasi via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={onClose}
                >
                  Pesan dengan Service
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartDetailModal;